import { clsx } from "clsx";
import { Cpu, MemoryStick, HardDrive } from "lucide-react";
import type { TenantQuota } from "@/types";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";

interface QuotaDisplayProps {
  quota: TenantQuota | null;
  isLoading: boolean;
}

interface QuotaBarProps {
  label: string;
  used: number;
  total: number;
  unit: string;
  icon: React.ReactNode;
  color: string;
}

function QuotaBar({ label, used, total, unit, icon, color }: QuotaBarProps) {
  const percent = total > 0 ? (used / total) * 100 : 0;
  const isWarning = percent > 80;
  const isCritical = percent > 95;

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-gray-300">{label}</span>
        </div>
        <span className="text-sm text-gray-400">
          {used} / {total} {unit}
        </span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-gray-800">
        <div
          className={clsx(
            "h-full rounded-full transition-all",
            isCritical
              ? "bg-red-500"
              : isWarning
                ? "bg-yellow-500"
                : color
          )}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <p className="mt-1 text-right text-xs text-gray-500">
        {percent.toFixed(1)}% used
      </p>
    </div>
  );
}

export function QuotaDisplay({ quota, isLoading }: QuotaDisplayProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!quota) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6 text-center">
        <p className="text-sm text-gray-400">Quota information unavailable.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
      <h3 className="mb-5 text-lg font-semibold text-gray-200">
        Tenant Quota
      </h3>
      <div className="space-y-5">
        <QuotaBar
          label="CPU Cores"
          used={quota.used_vcpu}
          total={quota.quota_vcpu}
          unit="cores"
          icon={<Cpu className="h-4 w-4 text-cyan-400" />}
          color="bg-cyan-500"
        />
        <QuotaBar
          label="RAM"
          used={Math.round(quota.used_ram_gb)}
          total={quota.quota_ram_gb}
          unit="GB"
          icon={<MemoryStick className="h-4 w-4 text-purple-400" />}
          color="bg-purple-500"
        />
        <QuotaBar
          label="Disk"
          used={quota.used_disk_gb}
          total={quota.quota_disk_gb}
          unit="GB"
          icon={<HardDrive className="h-4 w-4 text-green-400" />}
          color="bg-green-500"
        />
      </div>
    </div>
  );
}
